import { CalendarDate, Time } from '@internationalized/date';
import { Maybe } from '@passionware/monads';
import {
  Booking,
  CallsheetLocation,
  CustomFieldType,
  Production,
  Resource,
  ResourceSlotAssignment
} from '__types__';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import { v4 } from 'uuid';
import store from 'store';
import { getCustomFieldDefinitions } from 'store/v1/custom_fields/custom_fields.selectors';
import { makeGetSlotName } from 'store/v1/slot_categories/slot_categories.selectors.js';
import {
  bookingConfirmedCheck,
  generateCallsheetTitleFromProduction
} from 'v1/helpers/byModel/CallsheetHelper';
import { getEventEnd, getEventStart } from 'v1/helpers/byModel/EventHelper';
import { DEFAULT_START_TIME } from 'v1/helpers/byType/dateHelper';
import { GeneratedCallsheetData } from 'v5/api/callsheet/callsheet.api.http.converter';
import { CallsheetRecipient, CallsheetTeamMember } from '__types__';
import { parseEntityId } from 'v5/platform/lang/entityId';
import { resolveCustomFieldValue } from 'v5/core/custom-fields/resolveCustomFieldValue';
import {
  buildTeamMemberCreateRecipients,
  getDefaultCallsheetTeamMemberEmail,
  recipientsForTeamMember
} from 'v5/features/callsheets/callsheetTeamMemberEmails';
import { duplicateCallsheetLocations } from './duplicateCallsheetLocations';

export interface GeneratedCallsheetMeta {
  teamMemberCountByProductionId: Record<number, number>;
}

export interface GeneratedCallsheetResult {
  data: GeneratedCallsheetData;
  meta: GeneratedCallsheetMeta;
}

export interface PerDayBreakdown {
  date: CalendarDate;
  teamMemberCountByProductionId: Record<number, number>;
  teamMemberContactIds: number[];
  locationContactIds: number[];
}

export interface GeneratedCallsheetResultForRange {
  /** Aggregated data for the entire range (union of all days) */
  data: GeneratedCallsheetData;
  meta: GeneratedCallsheetMeta;
  /** Per-day breakdown for UI display */
  perDay: PerDayBreakdown[];
}

/**
 * Generates callsheet data structure from multiple productions for a given date.
 *
 * This function builds the preferred structure with root-level groups:
 * - Groups are created at root level with #id
 * - Sections reference groups via #ref in their groups array
 * - Team members are at root level and reference groups via group: {#ref}
 *
 * Process overview:
 * 1. Filter resource slots to find confirmed bookings for the selected date
 * 2. Identify groups that have confirmed slots
 * 3. Create groups at root level with #id
 * 4. Create sections with #id that reference groups via #ref
 * 5. Create team members at root level that reference groups via group: {#ref}
 * 6. Extract additional data like purchase order numbers from bookings
 * 7. Collect locations from confirmed location slots across all productions
 *
 * Structure (preferred pattern - groups at root):
 * ```
 * groups: [{
 *   "#id": "group-76",
 *   "name": "Locations",
 *   "order": 0
 * }],
 * sections: [{
 *   "#id": "section-27",
 *   "name": "Production 27",
 *   "groups": [{"#ref": "group-76"}]
 * }],
 * team_members: [{
 *   "group": {"#ref": "group-76"},
 *   "contact_id": 101,
 *   "display_role": "Photographer"
 * }]
 * ```
 *
 * @param productions - Array of production objects to generate data from
 * @param props.selectedDate - CalendarDate to filter confirmed slots
 * @param props.callTime - Optional call time to populate for each team_member
 *
 * @returns GeneratedCallsheetData with root-level groups and graph references
 */
export function generateCallsheetDataFromProductions(
  productions: Production[],
  bookingsStore: Record<Booking['id'], Booking>,
  props: {
    selectedDate: CalendarDate;
    callTime?: Time | null;
    /** Optional: Original team members to preserve customizations (display fields, notes, custom call times) */
    originalTeamMembers?: CallsheetTeamMember[];
    /** Optional: Flat GET recipients — used to preserve selected emails when regenerating */
    originalRecipients?: CallsheetRecipient[];
    /** Original locations to copy instead of collecting locations from bookings. */
    originalLocations?: CallsheetLocation[];
  }
): GeneratedCallsheetResult {
  const selectedDate = props.selectedDate.toString();
  const callTime = props.callTime?.toString();
  const originalTeamMembers = props.originalTeamMembers ?? [];
  const originalRecipients = props.originalRecipients;
  const duplicatedLocations = props.originalLocations
    ? duplicateCallsheetLocations(props.originalLocations)
    : undefined;

  // Extract contact data from bookings
  const contactsFromBookings: Record<number, Resource> = {};
  Object.values(bookingsStore).forEach(booking => {
    if (booking?.contact?.id) {
      contactsFromBookings[booking.contact.id] = booking.contact;
    }
  });

  // Create a map of original team members by contact_id for quick lookup
  const originalTeamMembersMap = new Map<number, CallsheetTeamMember>();
  for (const tm of originalTeamMembers) {
    if (tm.contact_id) {
      originalTeamMembersMap.set(tm.contact_id, tm);
    }
  }

  const bookings = {
    data: bookingsStore
  };

  // Early returns for invalid inputs
  if (!Array.isArray(productions) || productions.length === 0) {
    return {
      data: {
        groups: [],
        sections: [],
        team_members: [],
        locations: duplicatedLocations ?? []
      },
      meta: { teamMemberCountByProductionId: {} }
    };
  }

  if (!selectedDate) {
    return {
      data: {
        groups: [],
        sections: [],
        team_members: [],
        locations: duplicatedLocations ?? []
      },
      meta: { teamMemberCountByProductionId: {} }
    };
  }

  // Initialize data structures for building the callsheet:
  // - groups: Groups at root level with #id (preferred pattern)
  // - sections: Sections that reference groups via #ref
  // - team_members: Team members at root level that reference groups via group: {#ref}
  // - locations: Locations collected from confirmed location slots across all productions
  // - teamMemberCountByProductionId: UI-only mapping for display purposes
  const state = store.getState();
  const groups: any[] = [];
  const sections: any[] = [];
  const team_members: any[] = [];
  const locations: any[] = [];
  const teamMemberCountByProductionId: Record<number, number> = {};
  const allGroupsMap = new Map<number, string>(); // Track group.id -> groupAlias mapping
  const seenLocationContactIds = new Set<number>(); // Track contact IDs to deduplicate locations across productions

  // Load required data from Redux store for processing:
  // - getSlotName: Helper to get role names from slot categories
  // - statuses: Booking status information
  // - customFieldDefinitions: For extracting PO numbers from bookings
  // Note: makeGetSlotName is a selector that takes state and returns a function (slot) => name
  // Ensure state has slot_categories before calling the selector
  const getSlotName = state.slot_categories
    ? (makeGetSlotName as any)(state)
    : () => null; // Fallback if slot_categories not available
  const contacts = { data: contactsFromBookings };
  const statuses = get(state, 'statuses', {});
  const customFieldDefinitions = getCustomFieldDefinitions(state);

  // Process each production to build its section and associated team members
  productions.forEach((production, productionIndex) => {
    if (!production || isEmpty(production) || !production.id) {
      return;
    }

    const productionId = production.id;

    // Debug: Check if production has resource_slots
    // Note: Production objects from API might not include nested resource_slots by default
    // If resource_slots is missing, we won't be able to generate team members
    if (!production.resource_slots || production.resource_slots.length === 0) {
      // Still create a section for the production even if no resource slots
      const sectionTitle = generateCallsheetTitleFromProduction(production);
      const sectionAlias = `section-${productionId}`;
      sections.push({
        '#id': sectionAlias,
        name: sectionTitle,
        order: productionIndex,
        metadata: {
          production_id: productionId
        }
      });
      return;
    }

    // STEP 1: Filter resource slots to find confirmed bookings for the selected date
    // We iterate through all resource slots and filter assignments where:
    // 1. The booking is confirmed (status check)
    // 2. The booking has events that overlap with the selected date
    let confirmedSlots: any[] = [];
    if (production.resource_slots && production.resource_slots.length > 0) {
      const slots = orderBy(production.resource_slots, 'order');
      confirmedSlots = slots.reduce((arr, resourceSlot) => {
        // Filter assignments to only include confirmed bookings with valid date overlap
        const assignments = ((resourceSlot as any).resource_slot_assignments ||
          []) as ResourceSlotAssignment[];
        const validAssignments = assignments.filter((assignment: any) => {
          // Check if the booking has a CONFIRMED status
          const confirmed = bookingConfirmedCheck(assignment.booking_id, {
            bookings,
            statuses
          });
          const booking = get(bookings, ['data', assignment.booking_id], {});

          // Check if the assignment has events that overlap with the selected date
          // Match the logic from getConfirmedSlots selector (line 295)
          // Note: Original selector uses ['events', [0]] which seems incorrect, using ['events', 0] instead
          if (confirmed && booking && get(assignment, ['events', 0])) {
            const events = get(assignment, 'events', []);
            let isValid = false;
            events.forEach((event: any) => {
              const start = getEventStart(event);
              const end = getEventEnd(event);

              // Check if the selected date falls within the event's date range (inclusive)
              if (
                moment(selectedDate, 'YYYY-MM-DD').isBetween(
                  start,
                  end,
                  null,
                  '[]'
                )
              ) {
                isValid = true;
              }
            });
            return isValid;
          }
          return false;
        });

        // Only include slots that have at least one valid assignment
        if (!isEmpty(validAssignments)) {
          arr.push({
            ...resourceSlot,
            resource_slot_assignments: validAssignments
          });
        }
        return arr;
      }, [] as any[]);
    }
    // STEP 2: Handle case where production has no confirmed slots on the selected date
    // Still create a section for the production (for UI consistency), but with no groups or team members
    if (isEmpty(confirmedSlots)) {
      const sectionTitle = generateCallsheetTitleFromProduction(production);
      const sectionAlias = `section-${productionId}`;
      sections.push({
        '#id': sectionAlias,
        name: sectionTitle,
        order: productionIndex,
        metadata: {
          production_id: productionId
        }
      });
      return;
    }

    // STEP 3: Identify groups that are associated with confirmed slots
    // We need to find which groups from the production are actually being used
    const productionGroups = production.groups
      ? orderBy(
          production.groups.filter(group => !group.archived),
          'order'
        )
      : [];

    // Extract unique group IDs from all confirmed slots
    // This tells us which groups have confirmed bookings on the selected date
    const slotGroupIds = confirmedSlots.reduce((groupIds, slot) => {
      if (slot.group_id) {
        groupIds.add(slot.group_id);
      }
      return groupIds;
    }, new Set());

    // STEP 4: Create groups at root level with #id
    const sectionTitle = generateCallsheetTitleFromProduction(production);
    const sectionAlias = `section-${productionId}`;
    const sectionGroupRefs: any[] = [];

    productionGroups
      .filter(group => slotGroupIds.has(group.id))
      .forEach(group => {
        // Create unique alias for this group
        const groupAlias = `group-${group.id}`;

        // Track group alias to avoid duplicates and for team members to reference
        if (!allGroupsMap.has(group.id)) {
          allGroupsMap.set(group.id, groupAlias);

          // Create group at root level with #id
          groups.push({
            '#id': groupAlias,
            name: group.name, // Temporarily prepend production name
            order: group.order
          });
        }

        // Collect group reference for the section
        sectionGroupRefs.push({ '#ref': groupAlias });
      });

    // STEP 5: Create section that references groups via #ref
    sections.push({
      '#id': sectionAlias,
      name: sectionTitle,
      description: production.description,
      order: productionIndex,
      metadata: {
        production_id: productionId
      },
      groups: sectionGroupRefs // Reference groups from root level
    });

    // STEP 6: Generate team members at root level, referencing groups via group: {#ref}
    let teamMemberOrder = team_members.length;
    confirmedSlots.forEach(resourceSlot => {
      resourceSlot.resource_slot_assignments.forEach((assignment: any) => {
        // Look up the booking to extract purchase order information
        const booking = get(bookings, ['data', assignment.booking_id], null);

        // Extract purchase order number from booking custom fields if it exists
        let purchaseOrderNumber: Maybe<string> = null;
        if (booking) {
          const bookingCustomFields = get(
            booking,
            'booking_type.metastructure.fields',
            []
          );
          const purchaseOrderFieldDefinition = bookingCustomFields.find(
            (bookingCustomField: any) => {
              return !!(customFieldDefinitions || []).find(
                ({ id, data_type }) =>
                  id === bookingCustomField.custom_field_definition_id &&
                  data_type === CustomFieldType.NETSUITE_PO
              );
            }
          );
          if (purchaseOrderFieldDefinition) {
            // Phase 1: booking CF values are global-only (no workspace lens).
            const purchaseOrderField = resolveCustomFieldValue(
              booking.custom_fields,
              // @ts-expect-error -- this is preserved from callsheet.selectors.js
              purchaseOrderFieldDefinition.custom_field_definition_id,
              null
            );
            purchaseOrderNumber = purchaseOrderField?.string_value as
              | string
              | null
              | undefined;
          }
        }

        // Look up contact information for this assignment
        const contact = get(contacts, ['data', assignment.contact_id]);
        if (!contact) {
          return;
        }

        // Build team member at root level, referencing group via group: {#ref}.
        // First recipient: preserve original selections when regenerating/duplicating,
        // otherwise default to the first selectable contact/agent email.
        const contactId = get(assignment, 'contact_id');
        const groupId = resourceSlot.group_id;
        const groupAlias = allGroupsMap.get(groupId);

        if (!groupAlias) {
          // Should not happen, but safety check
          return;
        }

        // Check if this team member exists in the original callsheet
        const originalTeamMember = originalTeamMembersMap.get(contactId);

        // Build new team member data from bookings
        const newTeamMember = {
          group: { '#ref': groupAlias }, // Reference group via #ref
          order: teamMemberOrder,
          display_role: getSlotName(resourceSlot),
          display_name: get(contact, 'full_name', ''),
          display_phone_number: get(contact, ['phone_numbers', 0, 'value_1']),
          contact_details_visible: true,
          display_profile_picture: get(contact, 'profile_picture'),
          display_purchase_order_number: purchaseOrderNumber,
          call_time: callTime || DEFAULT_START_TIME,
          contact_id: contactId,
          recipients: buildTeamMemberCreateRecipients({
            contactId,
            recipients: recipientsForTeamMember(
              originalRecipients,
              originalTeamMember?.id
            ),
            fallbackEmail: getDefaultCallsheetTeamMemberEmail(contact)
          })
        };

        // If original team member exists, merge to preserve customizations
        // Original data takes precedence for display fields, notes, custom call times
        // New data provides updated purchase order numbers and group references
        if (originalTeamMember) {
          // Preserve original customizations (display fields, notes, custom call times)
          // but use new data for group references and purchase order numbers
          const originalAny = originalTeamMember as any; // Type assertion for fields that may exist
          team_members.push({
            ...newTeamMember,
            // Preserve original customizations
            display_name:
              originalTeamMember.display_name ?? newTeamMember.display_name,
            display_phone_number:
              originalTeamMember.display_phone_number ??
              newTeamMember.display_phone_number,
            display_role:
              originalTeamMember.display_role ?? newTeamMember.display_role,
            display_profile_picture:
              originalTeamMember.display_profile_picture ??
              newTeamMember.display_profile_picture,
            display_note: originalAny.display_note,
            note: originalTeamMember.note,
            // Preserve custom call time if set in original, otherwise use new/default
            call_time: originalTeamMember.call_time || newTeamMember.call_time,
            contact_details_visible:
              originalTeamMember.contact_details_visible ??
              newTeamMember.contact_details_visible,
            display_purchase_order_number:
              newTeamMember.display_purchase_order_number ||
              originalAny.display_purchase_order_number ||
              null
          });
        } else {
          team_members.push(newTeamMember);
        }

        // Track count per production for UI display
        teamMemberCountByProductionId[productionId] =
          (teamMemberCountByProductionId[productionId] || 0) + 1;

        teamMemberOrder++;
      });
    });

    // STEP 7: Collect locations from location slots
    // Location slots are slots where is_location is true
    // Extract location data from the contact's locations array
    // Deduplicate by contact_id since the same location contact may appear in multiple productions
    confirmedSlots
      .filter((resourceSlot: any) => resourceSlot.is_location)
      .forEach((locationSlot: any) => {
        locationSlot.resource_slot_assignments.forEach((assignment: any) => {
          const contactId = assignment.contact_id;

          // Skip if we've already added a location for this contact
          if (seenLocationContactIds.has(contactId)) {
            return;
          }

          const contact = get(contacts, ['data', contactId], {});

          if (contact) {
            const locationLabel = get(contact, 'full_name') || null;
            const locationName = get(contact, ['locations', 0, 'name']) || null;
            const locationCoordinates =
              get(contact, ['locations', 0, 'coordinates']) || null;

            // Only add location if it has meaningful data
            if (locationLabel || locationName || locationCoordinates) {
              seenLocationContactIds.add(contactId);
              const location = {
                id: `temp-${v4()}`,
                order: locations.length,
                note: '',
                location_label: locationLabel,
                location_name: locationName,
                location_coordinates: locationCoordinates
              };
              locations.push(location);
            }
          }
        });
      });
  });

  // Return the complete structure (preferred pattern):
  // - data: groups, sections, team_members, locations (sent to backend)
  // - meta: UI-only data like teamMemberCountByProductionId
  return {
    data: {
      groups,
      sections,
      team_members,
      locations: duplicatedLocations ?? locations
    },
    meta: {
      teamMemberCountByProductionId
    }
  };
}

export function extractBookingIdsFromProductions(
  productions: Production[]
): Booking['id'][] {
  return productions.flatMap(production =>
    (production.resource_slots ?? []).flatMap(slot =>
      ((slot as any).resource_slot_assignments ?? []).flatMap(
        (assignment: ResourceSlotAssignment) => {
          const bookingId = parseEntityId(assignment.booking_id);
          return bookingId === null ? [] : [bookingId];
        }
      )
    )
  );
}

/**
 * Generates callsheet data for a date range by aggregating results from each day.
 * Deduplicates team members and locations across the range while preserving per-day breakdown.
 */
export function generateCallsheetDataFromProductionsForRange(
  productions: Production[],
  bookingsStore: Record<Booking['id'], Booking>,
  props: {
    dateRange: { from: CalendarDate; to: CalendarDate };
    callTime?: Time | null;
    /** Optional: Original team members to preserve customizations (display fields, notes, custom call times) */
    originalTeamMembers?: CallsheetTeamMember[];
    /** Optional: Flat GET recipients — used to preserve selected emails when regenerating */
    originalRecipients?: CallsheetRecipient[];
    /** Original locations to copy instead of collecting locations from bookings. */
    originalLocations?: CallsheetLocation[];
  }
): GeneratedCallsheetResultForRange {
  const { dateRange, callTime, originalTeamMembers, originalRecipients } =
    props;
  const duplicatedLocations = props.originalLocations
    ? duplicateCallsheetLocations(props.originalLocations)
    : undefined;

  // Early return for empty inputs
  if (!Array.isArray(productions) || productions.length === 0) {
    return {
      data: {
        groups: [],
        sections: [],
        team_members: [],
        locations: duplicatedLocations ?? []
      },
      meta: { teamMemberCountByProductionId: {} },
      perDay: []
    };
  }

  // Collect unique dates from booking events that fall within the range
  const rangeFromStr = dateRange.from.toString();
  const rangeToStr = dateRange.to.toString();
  const relevantDatesSet = new Set<string>();
  const rangeStart = moment(rangeFromStr, 'YYYY-MM-DD');
  const rangeEnd = moment(rangeToStr, 'YYYY-MM-DD');

  // Collect dates from resource_slot_assignments in productions
  productions.forEach(production => {
    if (!production?.resource_slots) return;

    production.resource_slots.forEach((slot: any) => {
      const assignments = slot.resource_slot_assignments || [];
      assignments.forEach((assignment: any) => {
        const events = assignment.events || [];
        events.forEach((event: any) => {
          const start = getEventStart(event);
          const end = getEventEnd(event);

          if (!start || !end) return;

          const eventStart = moment(start);
          const eventEnd = moment(end);

          // Find the overlap between event dates and our range
          const overlapStart = eventStart.isAfter(rangeStart)
            ? eventStart
            : rangeStart.clone();
          const overlapEnd = eventEnd.isBefore(rangeEnd)
            ? eventEnd
            : rangeEnd.clone();

          // Only add days if there's an actual overlap
          if (overlapStart.isSameOrBefore(overlapEnd, 'day')) {
            const current = overlapStart.clone();
            while (current.isSameOrBefore(overlapEnd, 'day')) {
              relevantDatesSet.add(current.format('YYYY-MM-DD'));
              current.add(1, 'day');
            }
          }
        });
      });
    });
  });

  // Convert to CalendarDate array
  const dates: CalendarDate[] = Array.from(relevantDatesSet)
    .sort()
    .map(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new CalendarDate(year, month, day);
    });

  // If no relevant dates found, return empty result
  if (dates.length === 0) {
    return {
      data: {
        groups: [],
        sections: [],
        team_members: [],
        locations: duplicatedLocations ?? []
      },
      meta: { teamMemberCountByProductionId: {} },
      perDay: []
    };
  }

  // Generate per-day results only for relevant dates
  const perDayResults = dates.map(date => ({
    date,
    result: generateCallsheetDataFromProductions(productions, bookingsStore, {
      selectedDate: date,
      callTime,
      originalTeamMembers,
      originalRecipients
    })
  }));

  // Build per-day breakdown
  const perDay: PerDayBreakdown[] = perDayResults.map(({ date, result }) => ({
    date,
    teamMemberCountByProductionId: result.meta.teamMemberCountByProductionId,
    teamMemberContactIds: (result.data.team_members ?? []).map(
      tm => tm.contact_id
    ),
    locationContactIds: (result.data.locations ?? [])
      .map(loc => loc.contact_id)
      .filter((id): id is number => id != null)
  }));

  // Aggregate and deduplicate across all days
  const seenTeamMemberKeys = new Set<string>(); // "contactId-groupRef" to dedupe same person in same group
  const seenLocationContactIds = new Set<number>();
  const seenGroupIds = new Set<string>();
  const seenSectionIds = new Set<string>();

  const aggregatedGroups: any[] = [];
  const aggregatedSections: any[] = [];
  const aggregatedTeamMembers: any[] = [];
  const aggregatedLocations: any[] = [];
  const aggregatedTeamMemberCountByProductionId: Record<number, number> = {};

  for (const { result } of perDayResults) {
    // Aggregate groups (dedupe by #id)
    for (const group of result.data.groups ?? []) {
      const groupId = group['#id'] as string | undefined;
      if (groupId && !seenGroupIds.has(groupId)) {
        seenGroupIds.add(groupId);
        aggregatedGroups.push(group);
      }
    }

    // Aggregate sections (dedupe by #id)
    for (const section of result.data.sections ?? []) {
      const sectionId = section['#id'] as string | undefined;
      if (sectionId && !seenSectionIds.has(sectionId)) {
        seenSectionIds.add(sectionId);
        aggregatedSections.push(section);
      }
    }

    // Aggregate team members (dedupe by contact_id + group ref)
    for (const teamMember of result.data.team_members ?? []) {
      const groupRef = teamMember.group?.['#ref'] ?? '';
      const key = `${teamMember.contact_id}-${groupRef}`;
      if (!seenTeamMemberKeys.has(key)) {
        seenTeamMemberKeys.add(key);
        aggregatedTeamMembers.push({
          ...teamMember,
          order: aggregatedTeamMembers.length
        });
      }
    }

    // Aggregate locations (dedupe by contact_id from location_label lookup)
    for (const location of result.data.locations ?? []) {
      // Locations don't have contact_id directly, use location_label as proxy for deduplication
      // Since we already dedupe by seenLocationContactIds in the single-day function,
      // we need a different approach here - use a composite key
      const locationKey = `${location.location_label}-${location.location_name}-${JSON.stringify(location.location_coordinates)}`;
      const hash = locationKey
        .split('')
        .reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0);
      if (!seenLocationContactIds.has(hash)) {
        seenLocationContactIds.add(hash);
        aggregatedLocations.push({
          ...location,
          order: aggregatedLocations.length
        });
      }
    }
  }

  // Calculate team member count per production
  // Use per-day counts and take max per production (since same person on multiple days = 1 person)
  // Then verify against deduplicated team members
  const contactsPerProduction = new Map<number, Set<number>>();

  // First, collect from per-day results to get production mapping
  for (const { result } of perDayResults) {
    for (const teamMember of result.data.team_members ?? []) {
      // Each day's team members have the correct production association
      // We need to find which production this team member belongs to
      const groupRef = teamMember.group?.['#ref'];
      if (!groupRef || !teamMember.contact_id) continue;

      // Find production from section metadata
      for (const section of result.data.sections ?? []) {
        const productionId = section.metadata?.production_id as
          | number
          | undefined;
        if (!productionId) continue;
        const sectionGroups = section.groups ?? [];
        const hasGroup = sectionGroups.some((g: any) => g['#ref'] === groupRef);
        if (hasGroup) {
          if (!contactsPerProduction.has(productionId)) {
            contactsPerProduction.set(productionId, new Set());
          }
          contactsPerProduction.get(productionId)!.add(teamMember.contact_id);
          break;
        }
      }
    }
  }

  for (const [productionId, contacts] of contactsPerProduction) {
    aggregatedTeamMemberCountByProductionId[productionId] = contacts.size;
  }

  return {
    data: {
      groups: aggregatedGroups,
      sections: aggregatedSections,
      team_members: aggregatedTeamMembers,
      locations: duplicatedLocations ?? aggregatedLocations
    },
    meta: {
      teamMemberCountByProductionId: aggregatedTeamMemberCountByProductionId
    },
    perDay
  };
}
